/**
 * UndoContext - P2.3 Undo + Hotkeys
 * 
 * Central undo system for Granullo. Provides time-bound undo for
 * user-initiated, potentially destructive actions.
 * 
 * IMPORTANT: Undo affects Granullo-side state only (drafts, config, 
 * invitations, tags, attachments, notes). Undo does NOT reverse 
 * bank settlement or real-world transfers.
 * 
 * Usage:
 *   const { performWithUndo, undo, canUndo, lastAction } = useUndo();
 *   
 *   await performWithUndo({
 *     do: () => deleteProgram(id),
 *     undo: () => restoreProgram(id, previousData),
 *     label: 'Removed program',
 *     description: 'Education Support FY25',
 *     ttlMs: 10000, // 10 seconds
 *   });
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { useHotkeys } from './HotkeyContext';

// Default TTL for undo actions (10 seconds)
const DEFAULT_UNDO_TTL = 10000;

// Action types that are explicitly NOT undoable
const NON_UNDOABLE_ACTIONS = [
  'export_download',
  'view_navigation',
  'authentication',
  'bank_settlement',
  'ledger_transaction',
  'audit_log_write',
];

// Create the context
const UndoContext = createContext(null);

/**
 * UndoProvider - Wrap your app with this to enable undo functionality
 */
export function UndoProvider({ children }) {
  // Current undoable action (single-level for MVP)
  const [lastAction, setLastAction] = useState(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const timerRef = useRef(null);
  const toastIdRef = useRef(null);
  
  // Get hotkey context for registration
  const { registerUndo } = useHotkeys();

  // Clear the undo action and timer
  const clearUndo = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setLastAction(null);
  }, []);

  // Check if undo is available
  const canUndo = lastAction !== null && !isUndoing;

  /**
   * Perform an action with undo capability
   * 
   * @param {Object} options
   * @param {Function} options.do - The action to perform
   * @param {Function} options.undo - The function to reverse the action
   * @param {string} options.label - Short description (e.g., "Removed beneficiary")
   * @param {string} options.description - Details (e.g., "John D.")
   * @param {number} options.ttlMs - Time-to-live in milliseconds (default 10s)
   * @param {string} options.scope - Optional scope identifier
   * @param {Function} options.onUndo - Optional callback after undo
   * @param {Function} options.onExpire - Optional callback when TTL expires
   */
  const performWithUndo = useCallback(async (options) => {
    const {
      do: doAction,
      undo: undoAction,
      label,
      description = '',
      ttlMs = DEFAULT_UNDO_TTL,
      scope = 'global',
      onUndo,
      onExpire,
    } = options;

    // Clear any existing undo action
    clearUndo();
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
    }

    try {
      // Perform the action
      const result = await doAction();

      // Store the undo action
      const action = {
        id: Date.now(),
        label,
        description,
        undoFn: undoAction,
        scope,
        onUndo,
        onExpire,
        createdAt: Date.now(),
        ttlMs,
        result,
      };
      setLastAction(action);

      // Show toast with undo button
      const toastMessage = description 
        ? `${label}: ${description}`
        : label;

      toastIdRef.current = toast(toastMessage, {
        duration: ttlMs,
        action: {
          label: 'Undo',
          onClick: () => {
            performUndo();
          },
        },
        onDismiss: () => {
          // Only trigger expire if not undone
          if (lastAction?.id === action.id) {
            onExpire?.();
            setLastAction(null);
          }
        },
        description: `${Math.round(ttlMs / 1000)}s to undo`,
      });

      // Set expiration timer
      timerRef.current = setTimeout(() => {
        setLastAction((current) => {
          if (current?.id === action.id) {
            onExpire?.();
            return null;
          }
          return current;
        });
      }, ttlMs);

      return result;
    } catch (error) {
      console.error('Action failed:', error);
      toast.error('Action failed', {
        description: error.message || 'Please try again',
      });
      throw error;
    }
  }, [clearUndo]);

  /**
   * Perform undo of the last action
   */
  const performUndo = useCallback(async () => {
    if (!lastAction || isUndoing) {
      if (!lastAction) {
        toast('Nothing to undo', {
          description: 'No recent action available',
          duration: 2000,
        });
      }
      return false;
    }

    setIsUndoing(true);
    
    try {
      // Execute the undo function
      await lastAction.undoFn(lastAction.result);
      
      // Show success toast
      toast.success(`Restored: ${lastAction.description || lastAction.label}`, {
        duration: 3000,
      });

      // Call onUndo callback if provided
      lastAction.onUndo?.();

      // Clear the action
      clearUndo();
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }

      return true;
    } catch (error) {
      console.error('Undo failed:', error);
      toast.error('Could not undo', {
        description: error.message || 'The action could not be reversed',
      });
      return false;
    } finally {
      setIsUndoing(false);
    }
  }, [lastAction, isUndoing, clearUndo]);

  // Alias for performUndo
  const undo = performUndo;

  // Register undo function with HotkeyContext for Cmd/Ctrl+Z
  useEffect(() => {
    registerUndo(performUndo, canUndo);
  }, [registerUndo, performUndo, canUndo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const value = {
    performWithUndo,
    undo,
    canUndo,
    lastAction,
    isUndoing,
    clearUndo,
    NON_UNDOABLE_ACTIONS,
  };

  return (
    <UndoContext.Provider value={value}>
      {children}
    </UndoContext.Provider>
  );
}

/**
 * useUndo hook - Access undo functionality in components
 */
export function useUndo() {
  const context = useContext(UndoContext);
  if (!context) {
    throw new Error('useUndo must be used within an UndoProvider');
  }
  return context;
}

/**
 * Destructive action types for documentation and validation
 */
export const DESTRUCTIVE_ACTIONS = {
  // Programs
  DELETE_PROGRAM: 'delete_program',
  ARCHIVE_PROGRAM: 'archive_program',
  RESET_PROGRAM_TYPE: 'reset_program_type',
  
  // Beneficiaries
  REMOVE_BENEFICIARY: 'remove_beneficiary',
  
  // Partners
  REMOVE_PARTNER: 'remove_partner',
  
  // Invites
  REVOKE_INVITE: 'revoke_invite',
  
  // Notes & Documents
  DELETE_NOTE: 'delete_note',
  DELETE_EVIDENCE: 'delete_evidence',
  DELETE_DOCUMENT: 'delete_document',
  
  // Rules & Constraints
  REMOVE_RULE: 'remove_rule',
  REMOVE_CONSTRAINT: 'remove_constraint',
  
  // Views & Filters
  CLEAR_FILTERS: 'clear_filters',
  CLEAR_SAVED_VIEW: 'clear_saved_view',
  
  // Settings
  DISCONNECT_INTEGRATION: 'disconnect_integration',
  
  // Tags
  REMOVE_TAG: 'remove_tag',
  
  // Bulk operations
  BULK_DELETE: 'bulk_delete',
  BULK_ARCHIVE: 'bulk_archive',
};

export default UndoContext;
